import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import api from '../services/api';

const PRIMARY = '#00A7E3';
const ACCENT = '#102E5C';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. The link may have expired.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 1 }}>
            Invalid reset link
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            This reset link is invalid or has expired. New resets use a 6-digit code by email instead of a link.
          </Typography>
          <Link component={RouterLink} to="/forgot-password" sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600 }}>
            Forgot password — send code
          </Link>
        </Box>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <CheckCircle sx={{ fontSize: 64, color: '#10B981', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 1 }}>
            Password reset successful
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
            Your password has been updated. You can now sign in with your new password.
          </Typography>
          <Button
            component={RouterLink} to="/login" variant="contained"
            sx={{
              py: 1.25, px: 4, bgcolor: PRIMARY, borderRadius: 1,
              fontSize: '14px', fontWeight: 600, textTransform: 'none',
              boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
            }}
          >
            Go to Sign in
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 0.5 }}>
          Set a new password
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Your new password must be at least 6 characters.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={password} onChange={(e) => setPassword(e.target.value)}
            required size="small"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth label="Confirm New Password"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            required size="small"
            sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          />

          <Button
            type="submit" fullWidth variant="contained" disabled={loading}
            sx={{
              py: 1.25, bgcolor: PRIMARY, borderRadius: 1,
              fontSize: '14px', fontWeight: 600, textTransform: 'none',
              boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Reset password'}
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default ResetPassword;
